import { Option, Value } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import LoadingComponent from 'components/common/loading';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import { differenceInCalendarDays, isSameDay } from 'date-fns';
import useGetHotelRoom from 'hooks/use-get-hotel-room';
import { mealsOption } from 'legacy-modules/master/hotel/components/utils';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewHotelItem } from '../../hooks/use-get-default-values';
import {
  HotelPurchaseFormType,
  HotelPurchaseHotelItemFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetScheduleHotelTableList(hotelRooms: Option[]) {
  const { t } = useTranslation();
  const { editable } = useFormState();

  const columns = React.useMemo<
    IColumn<HotelPurchaseHotelItemFormType>[]
  >(() => {
    return [
      {
        header: t('common:check_in'),
        accessorKey: 'checkIn',
        minSize: 180,
        maxSize: 180,
        dataType: ColumnDataType.date,
        required: true,
      },
      {
        header: t('common:check_out'),
        accessorKey: 'checkOut',
        minSize: 180,
        maxSize: 180,
        dataType: ColumnDataType.date,
        required: true,
      },
      {
        header: t('common:meals'),
        accessorKey: 'detail',
        minSize: 100,
        maxSize: 150,
        dataType: ColumnDataType.select,
        options: mealsOption,
      },
      {
        header: t('common:pax'),
        accessorKey: 'pax',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
      },
      {
        header: t('common:number_of_night'),
        accessorKey: 'night',
        minSize: 100,
        dataType: ColumnDataType.null,
        disabled: true,
      },
      {
        header: t('common:number_of_room'),
        accessorKey: 'room',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      {
        header: t('common:room_type'),
        accessorKey: 'hotelRoomId',
        minSize: 180,
        dataType: ColumnDataType.select,
        options: hotelRooms,
        required: true,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
      ...(editable
        ? [
            {
              header: '',
              accessorKey: 'delete',
              minSize: 60,
              size: 60,
              maxSize: 60,
              dataType: ColumnDataType.delete,
            },
          ]
        : []),
    ];
  }, [editable, hotelRooms, t]);

  return columns;
}

export default function ScheduleHotelTable(props: Props) {
  const { asPath: path } = useRouter();
  const { editable } = useFormState();
  const [isShow, setIsShow] = React.useState(true);
  const name =
    `hotelPurchases.${props.parentIndex}.hotelPurchaseHotels` as const;

  const { trigger, getValues, control } =
    useFormContext<HotelPurchaseFormType>();

  const guestName =
    `hotelPurchases.${props.parentIndex}.hotelPurchaseGuests` as const;
  const numOfGuests = getValues(guestName).filter((g) => !!g.guestId).length;

  const { append, remove, update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const fields = getValues(name);

  React.useEffect(() => {
    if (path !== NavigationRoutes.HotelPurchasesCreate) return;
    fields.forEach((field, index) => {
      update(index, {
        ...field,
        pax: numOfGuests,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numOfGuests, update, path]);

  const { hotelId, hotelRooms, setHotelRooms, isFetching } = useGetHotelRoom({
    name: `hotelPurchases.${props.parentIndex}.hotelId`,
  });

  React.useEffect(() => {
    if (!hotelId || hotelId === '') {
      setHotelRooms([]);
      setIsShow(false);
      setTimeout(() => {
        setIsShow(true);
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  React.useEffect(() => {
    if (isFetching) {
      setIsShow(false);
    } else {
      setIsShow(true);
    }
  }, [isFetching]);

  const columns = useGetScheduleHotelTableList(hotelRooms);

  const dispatch = React.useCallback(
    (action) => {
      const hotels = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const hotel = hotels[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof HotelPurchaseHotelItemFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<HotelPurchaseHotelItemFormType> = () => {
        if (columnId === 'checkIn' || columnId === 'checkOut') {
          try {
            const checkIn = columnId === 'checkIn' ? value : hotel.checkIn;
            const checkOut = columnId === 'checkOut' ? value : hotel.checkOut;
            const night = isSameDay(checkOut, checkIn)
              ? 1
              : differenceInCalendarDays(checkOut, checkIn);
            if (night < 0 || !night) {
              return { night: 0 };
            } else {
              return { night };
            }
          } catch {
            return { night: 0 };
          }
        }
        if (columnId === 'hotelRoomId') {
          return { hotelRoomName: extra?.[0]?.label as string };
        }
        return {};
      };

      if (type === 'addRow') {
        append({ ...addNewHotelItem(), pax: numOfGuests });
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...hotel,
          [columnId]: value,
          ...otherVal(),
        });
        trigger(name);
      }
    },
    [append, getValues, name, numOfGuests, remove, trigger, update],
  );

  if (isFetching || !isShow) {
    return <LoadingComponent style={{ height: 100, width: '100%' }} />;
  }

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
